import type { ModeDefinition, Param, SliderParam, ColorParam, ColorRampParam, ToggleParam, SelectParam, TextParam } from '../modes/types';

// Lock icon SVGs
const LOCK_OPEN_SVG = `<svg class="lock-icon lock-open" viewBox="0 0 24 24" aria-hidden="true">
  <path d="M17 9h-6V7a3 3 0 0 1 5.5-1.5 1 1 0 1 0 1.7-1A5 5 0 0 0 9 7v2H7a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2Zm0 10H7v-8h10v8Z" />
</svg>`;

const LOCK_CLOSED_SVG = `<svg class="lock-icon lock-closed" viewBox="0 0 24 24" aria-hidden="true">
  <path d="M7 9V7a5 5 0 0 1 10 0v2h1a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h1Zm2 0h6V7a3 3 0 0 0-6 0v2Z" />
</svg>`;

const CHEVRON_SVG = `<svg class="chevron-icon" viewBox="0 0 24 24" aria-hidden="true">
  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
</svg>`;

export interface ControlCallbacks {
  onChange: (paramId: string, value: unknown) => void;
  onLockToggle: (paramId: string, locked: boolean) => void;
}

export interface ControlState {
  values: Record<string, unknown>;
  locks: Set<string>;
}

function createLockButton(paramId: string, locked: boolean): string {
  const lockedClass = locked ? 'locked' : '';
  return `
    <button class="lock-btn ${lockedClass}" type="button" data-lock="${paramId}" aria-label="Toggle lock">
      ${LOCK_OPEN_SVG}
      ${LOCK_CLOSED_SVG}
    </button>
  `;
}

function createSliderControl(param: SliderParam, value: number, locked: boolean): string {
  return `
    <div class="control" data-param-id="${param.id}">
      <div class="control-header">
        <label for="${param.id}" ${param.tooltip ? `title="${param.tooltip}"` : ''}>${param.label}</label>
        ${createLockButton(param.id, locked)}
      </div>
      <input id="${param.id}" type="range" min="${param.min}" max="${param.max}" step="${param.step}" value="${value}" />
    </div>
  `;
}

function createColorControl(param: ColorParam, value: string, locked: boolean): string {
  return `
    <div class="control" data-param-id="${param.id}">
      <div class="control-header">
        <label for="${param.id}" ${param.tooltip ? `title="${param.tooltip}"` : ''}>${param.label}</label>
        ${createLockButton(param.id, locked)}
      </div>
      <input id="${param.id}" type="color" value="${value}" />
    </div>
  `;
}

function createColorRampControl(param: ColorRampParam, value: [string, string, string], locked: boolean): string {
  return `
    <div class="control" data-param-id="${param.id}">
      <div class="control-header">
        <label ${param.tooltip ? `title="${param.tooltip}"` : ''}>${param.label}</label>
        ${createLockButton(param.id, locked)}
      </div>
      <div class="color-row">
        <input id="${param.id}-low" type="color" value="${value[0]}" data-ramp="${param.id}" data-index="0" aria-label="${param.label} low" />
        <input id="${param.id}-mid" type="color" value="${value[1]}" data-ramp="${param.id}" data-index="1" aria-label="${param.label} mid" />
        <input id="${param.id}-high" type="color" value="${value[2]}" data-ramp="${param.id}" data-index="2" aria-label="${param.label} high" />
      </div>
    </div>
  `;
}

function createToggleControl(param: ToggleParam, value: boolean, locked: boolean): string {
  return `
    <div class="control" data-param-id="${param.id}">
      <div class="control-header">
        <label for="${param.id}" ${param.tooltip ? `title="${param.tooltip}"` : ''}>${param.label}</label>
        ${createLockButton(param.id, locked)}
      </div>
      <label class="toggle-switch">
        <input type="checkbox" id="${param.id}" ${value ? 'checked' : ''} />
        <span class="toggle-slider"></span>
      </label>
    </div>
  `;
}

function createSelectControl(param: SelectParam, value: string, locked: boolean): string {
  const options = param.options.map(opt =>
    `<option value="${opt.value}" ${opt.value === value ? 'selected' : ''}>${opt.label}</option>`
  ).join('');

  return `
    <div class="control" data-param-id="${param.id}">
      <div class="control-header">
        <label for="${param.id}" ${param.tooltip ? `title="${param.tooltip}"` : ''}>${param.label}</label>
        ${createLockButton(param.id, locked)}
      </div>
      <select id="${param.id}">${options}</select>
    </div>
  `;
}

function createTextControl(param: TextParam, value: string, _locked: boolean): string {
  // Text controls (like seed) typically don't have locks
  return `
    <div class="control" data-param-id="${param.id}">
      <label for="${param.id}" ${param.tooltip ? `title="${param.tooltip}"` : ''}>${param.label}</label>
      <input id="${param.id}" type="text" value="${value}" ${param.placeholder ? `placeholder="${param.placeholder}"` : ''} />
    </div>
  `;
}

function createControl(param: Param, value: unknown, locked: boolean): string {
  switch (param.type) {
    case 'slider':
      return createSliderControl(param, value as number, locked);
    case 'color':
      return createColorControl(param, value as string, locked);
    case 'color-ramp':
      return createColorRampControl(param, value as [string, string, string], locked);
    case 'toggle':
      return createToggleControl(param, value as boolean, locked);
    case 'select':
      return createSelectControl(param, value as string, locked);
    case 'text':
      return createTextControl(param, value as string, locked);
  }
}

export function renderModeControls(
  container: HTMLElement,
  mode: ModeDefinition,
  state: ControlState,
  callbacks: ControlCallbacks,
): void {
  // Group params by their group
  const groupedParams = new Map<string, Param[]>();

  // Initialize groups in order
  for (const group of mode.groups) {
    groupedParams.set(group.id, []);
  }
  // Add ungrouped
  groupedParams.set('_ungrouped', []);

  // Sort params into groups
  for (const param of mode.params) {
    const groupId = param.group || '_ungrouped';
    const group = groupedParams.get(groupId);
    if (group) {
      group.push(param);
    } else {
      groupedParams.get('_ungrouped')!.push(param);
    }
  }

  // Build HTML
  let html = '';

  for (const group of mode.groups) {
    const params = groupedParams.get(group.id) || [];
    if (params.length === 0) continue;

    const isCollapsible = group.collapsible && group.id !== 'general';
    const isCollapsed = isCollapsible && group.collapsedByDefault;
    const collapsedClass = isCollapsed ? 'collapsed' : '';

    html += `<div class="control-group ${collapsedClass}" data-group="${group.id}">`;

    // Add group header
    if (group.label && group.id !== 'general') {
      if (isCollapsible) {
        html += `<button class="control-group-header collapsible" type="button" data-toggle-group="${group.id}" aria-expanded="${!isCollapsed}">
          ${CHEVRON_SVG}
          <span>${group.label}</span>
        </button>`;
      } else {
        html += `<div class="control-group-header">${group.label}</div>`;
      }
    }

    // Wrap controls in collapsible content with inner wrapper for animation
    html += `<div class="control-group-content"><div class="control-group-inner">`;
    for (const param of params) {
      const value = state.values[param.id] ?? param.default;
      const locked = state.locks.has(param.id);
      html += createControl(param, value, locked);
    }
    html += '</div></div>';

    html += '</div>';
    html += '<div class="divider" aria-hidden="true"></div>';
  }

  // Render ungrouped params
  const ungrouped = groupedParams.get('_ungrouped') || [];
  if (ungrouped.length > 0) {
    html += '<div class="control-group">';
    for (const param of ungrouped) {
      const value = state.values[param.id] ?? param.default;
      const locked = state.locks.has(param.id);
      html += createControl(param, value, locked);
    }
    html += '</div>';
  }

  container.innerHTML = html;

  // Attach event listeners
  attachControlListeners(container, mode, state, callbacks);
}

function attachControlListeners(
  container: HTMLElement,
  mode: ModeDefinition,
  state: ControlState,
  callbacks: ControlCallbacks,
): void {
  // Collapsible group toggles
  container.querySelectorAll<HTMLButtonElement>('[data-toggle-group]').forEach(btn => {
    btn.addEventListener('click', () => {
      const groupId = btn.dataset.toggleGroup;
      if (!groupId) return;
      const groupEl = container.querySelector<HTMLElement>(`[data-group="${groupId}"]`);
      if (!groupEl) return;

      const isCollapsed = groupEl.classList.toggle('collapsed');
      btn.setAttribute('aria-expanded', String(!isCollapsed));
    });
  });

  // Lock buttons
  container.querySelectorAll<HTMLButtonElement>('.lock-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const paramId = btn.dataset.lock;
      if (!paramId) return;
      const newLocked = !state.locks.has(paramId);
      if (newLocked) {
        state.locks.add(paramId);
        btn.classList.add('locked');
      } else {
        state.locks.delete(paramId);
        btn.classList.remove('locked');
      }
      callbacks.onLockToggle(paramId, newLocked);
    });
  });

  // Input controls
  for (const param of mode.params) {
    switch (param.type) {
      case 'slider': {
        const input = container.querySelector<HTMLInputElement>(`#${param.id}`);
        if (input) {
          const handler = () => {
            callbacks.onChange(param.id, Number(input.value));
          };
          input.addEventListener('input', handler);
          input.addEventListener('change', handler);
        }
        break;
      }

      case 'color': {
        const input = container.querySelector<HTMLInputElement>(`#${param.id}`);
        if (input) {
          input.addEventListener('input', () => {
            callbacks.onChange(param.id, input.value);
          });
        }
        break;
      }

      case 'color-ramp': {
        const inputs = container.querySelectorAll<HTMLInputElement>(`[data-ramp="${param.id}"]`);
        inputs.forEach(input => {
          input.addEventListener('input', () => {
            const ramp = [
              (container.querySelector<HTMLInputElement>(`#${param.id}-low`)?.value || '#000000'),
              (container.querySelector<HTMLInputElement>(`#${param.id}-mid`)?.value || '#000000'),
              (container.querySelector<HTMLInputElement>(`#${param.id}-high`)?.value || '#000000'),
            ] as [string, string, string];
            callbacks.onChange(param.id, ramp);
          });
        });
        break;
      }

      case 'toggle': {
        const input = container.querySelector<HTMLInputElement>(`#${param.id}`);
        if (input) {
          input.addEventListener('change', () => {
            callbacks.onChange(param.id, input.checked);
          });
        }
        break;
      }

      case 'select': {
        const input = container.querySelector<HTMLSelectElement>(`#${param.id}`);
        if (input) {
          input.addEventListener('change', () => {
            callbacks.onChange(param.id, input.value);
          });
        }
        break;
      }

      case 'text': {
        const input = container.querySelector<HTMLInputElement>(`#${param.id}`);
        if (input) {
          input.addEventListener('change', () => {
            callbacks.onChange(param.id, input.value);
          });
        }
        break;
      }
    }
  }
}

// Update control values without re-rendering (for randomization)
export function updateControlValues(
  container: HTMLElement,
  mode: ModeDefinition,
  values: Record<string, unknown>,
): void {
  for (const param of mode.params) {
    const value = values[param.id];
    if (value === undefined) continue;

    switch (param.type) {
      case 'slider': {
        const input = container.querySelector<HTMLInputElement>(`#${param.id}`);
        if (input) input.value = String(value);
        break;
      }

      case 'color': {
        const input = container.querySelector<HTMLInputElement>(`#${param.id}`);
        if (input) input.value = value as string;
        break;
      }

      case 'color-ramp': {
        const ramp = value as [string, string, string];
        const lowInput = container.querySelector<HTMLInputElement>(`#${param.id}-low`);
        const midInput = container.querySelector<HTMLInputElement>(`#${param.id}-mid`);
        const highInput = container.querySelector<HTMLInputElement>(`#${param.id}-high`);
        if (lowInput) lowInput.value = ramp[0];
        if (midInput) midInput.value = ramp[1];
        if (highInput) highInput.value = ramp[2];
        break;
      }

      case 'toggle': {
        const input = container.querySelector<HTMLInputElement>(`#${param.id}`);
        if (input) input.checked = value as boolean;
        break;
      }

      case 'select': {
        const input = container.querySelector<HTMLSelectElement>(`#${param.id}`);
        if (input) input.value = value as string;
        break;
      }

      case 'text': {
        const input = container.querySelector<HTMLInputElement>(`#${param.id}`);
        if (input) input.value = value as string;
        break;
      }
    }
  }
}
